/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Box, MenuItem, Paper, Select, Skeleton, TextField, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/es";
import * as yup from "yup";
import { useFormik } from "formik";
import * as Sentry from "@sentry/react";
import { CountrySelect, FieldWrapper, PageHeader, StepActions } from "@components/ui_v2";
import { RootState } from "@store/store";
import { setModalErrorVisible } from "@store/slices/appSlice";
import {
  useLazyGetPersonByIdQuery,
  useLazyGetPersonsQuery,
  usePatchPersonMutation,
  usePostPersonMutation,
} from "@api/personasApi";
import {
  useLazyGetAddressTypeQuery,
  useLazyGetCivilStatusQuery,
  useLazyGetMatrimonialRegimesQuery,
  useLazyGetPersonTypeQuery,
  useLazyGetStatesQuery,
  useLazyGetTabsConfigQuery,
} from "@api/catalogs";
import { useLazyGetAddressQuery, usePatchAddressMutation, usePostAddressMutation } from "@api/address";
import { useLazyGetByCpQuery } from "@api/postalCodes";
import {
  findPersonasTab,
  hasEnabledPersonFields,
  resolveFieldsForPerson,
  resolvePersonFormsConfig,
} from "@components/PersonForm/personHelpers";
import {
  AddressFormValues,
  buildAddressPayload,
  buildInitialValues,
  buildPersonPatchPayload,
  buildValidationSchema,
  OTHER_COLONIA_ID,
  OTHER_PERSON_ID,
  PersonFormValues,
} from "@components/PersonForm/personFormSchema";
import { getFechaNacimientoMinima, getPersonFullName } from "@helpers/funciones";
import { AddressData, ApiError, PersonaStructure, PersonFormsConfig } from "@interfaces/response";
import { Address, CivilStatus, MatrimonialRegimesData, StateStructure } from "@interfaces/catalogs";
import { UUID } from "crypto";
import {
  ADDRESS_CODES,
  ALTO_SKELETON_EXPEDIENTE,
  BOFU_TABS_CONFIG,
  PATHS_EXPEDIENTE_V2,
  PERSON_CATALOG,
} from "@helpers/constantes";

const FIELD_GRID = { display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "16px" };

type AddressBlockProps = {
  prefix: "particular" | "operativo";
  title: string;
  formik: ReturnType<typeof useFormik<PersonFormValues>>;
  disabled: boolean;
  required?: boolean;
  onCpLookup: (cp: string, prefix: "particular" | "operativo", coloniaId?: number) => void;
  colonias: { id: number; name: string }[];
};

const AddressBlock: React.FC<AddressBlockProps> = ({
  prefix,
  title,
  formik,
  disabled,
  required = false,
  onCpLookup,
  colonias,
}) => {
  const values = formik.values[prefix];
  const touched = formik.touched[prefix] || {};
  const errors = (formik.errors[prefix] || {}) as Partial<Record<keyof AddressFormValues, string>>;

  const setField = (field: keyof AddressFormValues, value: string) => formik.setFieldValue(`${prefix}.${field}`, value);

  const err = (field: keyof AddressFormValues) => (touched[field] ? errors[field] : undefined);

  return (
    <Box>
      <Typography variant="body1" color="primary.dark" fontWeight={700} mb={2}>
        {title}
      </Typography>

      <Box sx={FIELD_GRID}>
        <FieldWrapper label="Código Postal" error={err("cp")}>
          <TextField
            fullWidth
            size="lendia_input"
            required={required}
            value={values.cp}
            disabled={disabled}
            inputProps={{ maxLength: 5 }}
            error={Boolean(err("cp"))}
            onChange={(e) => {
              const cp = e.target.value.replace(/[^0-9]/g, "");
              setField("cp", cp);
              if (cp.length === 5) onCpLookup(cp, prefix);
            }}
            onBlur={() => formik.setFieldTouched(`${prefix}.cp`, true)}
          />
        </FieldWrapper>

        <FieldWrapper label="Calle o avenida" error={err("street")}>
          <TextField
            fullWidth
            size="lendia_input"
            required={required}
            value={values.street}
            disabled={disabled}
            inputProps={{ maxLength: 100 }}
            error={Boolean(err("street"))}
            onChange={(e) => setField("street", e.target.value)}
            onBlur={() => formik.setFieldTouched(`${prefix}.street`, true)}
          />
        </FieldWrapper>

        <FieldWrapper label="No. Exterior" error={err("no_ext")}>
          <TextField
            fullWidth
            size="lendia_input"
            required={required}
            value={values.no_ext}
            disabled={disabled}
            error={Boolean(err("no_ext"))}
            onChange={(e) => setField("no_ext", e.target.value)}
            onBlur={() => formik.setFieldTouched(`${prefix}.no_ext`, true)}
          />
        </FieldWrapper>

        <FieldWrapper label="No. Interior">
          <TextField
            fullWidth
            size="lendia_input"
            value={values.no_int}
            disabled={disabled}
            onChange={(e) => setField("no_int", e.target.value)}
            onBlur={() => formik.setFieldTouched(`${prefix}.no_int`, true)}
          />
        </FieldWrapper>

        <FieldWrapper label="Colonia" error={err("colonia_id")} full>
          <Select
            fullWidth
            size="lendia_input"
            displayEmpty
            value={values.colonia_id}
            disabled={disabled}
            error={Boolean(err("colonia_id"))}
            onChange={(e) => setField("colonia_id", String(e.target.value))}
            onBlur={() => formik.setFieldTouched(`${prefix}.colonia_id`, true)}
          >
            {colonias.map((colonia) => (
              <MenuItem key={colonia.id} value={String(colonia.id)}>
                {colonia.name}
              </MenuItem>
            ))}
            <MenuItem value={OTHER_COLONIA_ID}>Otra</MenuItem>
          </Select>
        </FieldWrapper>

        {values.colonia_id === OTHER_COLONIA_ID && (
          <FieldWrapper label="Otra colonia" error={err("other_neighborhood")} full>
            <TextField
              fullWidth
              size="lendia_input"
              required={required}
              value={values.other_neighborhood}
              disabled={disabled}
              error={Boolean(err("other_neighborhood"))}
              onChange={(e) => setField("other_neighborhood", e.target.value)}
              onBlur={() => formik.setFieldTouched(`${prefix}.other_neighborhood`, true)}
            />
          </FieldWrapper>
        )}

        <FieldWrapper label="Ciudad">
          <TextField fullWidth size="lendia_input" value={values.city} disabled />
        </FieldWrapper>

        <FieldWrapper label="Municipio">
          <TextField fullWidth size="lendia_input" value={values.locality} disabled />
        </FieldWrapper>

        <FieldWrapper label="Entidad" full>
          <TextField fullWidth size="lendia_input" value={values.state} disabled />
        </FieldWrapper>
      </Box>
    </Box>
  );
};

export const ExpFormularioPersona = () => {
  const { personaId: personId } = useParams<{ personaId: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const application = useSelector((state: RootState) => state.register.application);
  const clientDetails = useSelector((state: RootState) => state.register.personal_data);

  // Page-level state
  const [person, setPerson] = useState<PersonaStructure | null>(null);
  const [allPersons, setAllPersons] = useState<PersonaStructure[]>([]);
  const [formsConfig, setFormsConfig] = useState<PersonFormsConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Catalog & address state
  const [civilStatuses, setCivilStatuses] = useState<CivilStatus[]>([]);
  const [marriedId, setMarriedId] = useState("");
  const [regimes, setRegimes] = useState<MatrimonialRegimesData[]>([]);
  const [states, setStates] = useState<StateStructure[]>([]);
  const [particularAddress, setParticularAddress] = useState<AddressData | undefined>();
  const [operativoAddress, setOperativoAddress] = useState<AddressData | undefined>();
  const [particularType, setParticularType] = useState<Address | undefined>();
  const [operativoType, setOperativoType] = useState<Address | undefined>();

  // Form state
  const [isSaving, setIsSaving] = useState(false);
  const [particularColonias, setParticularColonias] = useState<{ id: number; name: string }[]>([]);
  const [operativoColonias, setOperativoColonias] = useState<{ id: number; name: string }[]>([]);

  // Computed fields (memos depend on person/allPersons/formsConfig)
  const personTypeCodes = useMemo(
    () => (person?.person_types_detail || []).map((item) => item.code),
    [person?.person_types_detail],
  );
  const principalTypeCodes = useMemo(() => {
    const principal = allPersons.find((c) => c.spouse_id === person?.id);
    return principal ? (principal.person_types_detail || []).map((item) => item.code) : [];
  }, [allPersons, person?.id]);
  const fields = useMemo(
    () => resolveFieldsForPerson(personTypeCodes, formsConfig ?? ({} as PersonFormsConfig), principalTypeCodes),
    [personTypeCodes, formsConfig, principalTypeCodes],
  );
  const spouseOptions = useMemo(() => {
    if (!fields.spouse_id?.enabled || !person) return [];
    return [
      ...allPersons.filter((item) => item.id !== person.id),
      { id: OTHER_PERSON_ID, name: "Otro", last_name: "", last_name_2: "" } as PersonaStructure,
    ];
  }, [fields.spouse_id?.enabled, allPersons, person?.id]);

  const fieldsRef = useRef(fields);
  fieldsRef.current = fields;
  const marriedIdRef = useRef(marriedId);
  marriedIdRef.current = marriedId;

  // API hooks
  const [getPersonById] = useLazyGetPersonByIdQuery();
  const [getPersons] = useLazyGetPersonsQuery();
  const [getTabsConfig] = useLazyGetTabsConfigQuery();
  const [getCivilStatus] = useLazyGetCivilStatusQuery();
  const [getMatrimonialRegimes] = useLazyGetMatrimonialRegimesQuery();
  const [getAddressType] = useLazyGetAddressTypeQuery();
  const [getAddress] = useLazyGetAddressQuery();
  const [getStates] = useLazyGetStatesQuery();
  const [getByCp] = useLazyGetByCpQuery();
  const [getPersonType] = useLazyGetPersonTypeQuery();
  const [patchPerson] = usePatchPersonMutation();
  const [postPerson] = usePostPersonMutation();
  const [postAddress] = usePostAddressMutation();
  const [patchAddress] = usePatchAddressMutation();

  const formik = useFormik<PersonFormValues>({
    initialValues: buildInitialValues(),
    enableReinitialize: false,
    validateOnMount: true,
    validateOnBlur: false,
    validationSchema: yup.lazy(() => buildValidationSchema(fieldsRef.current, marriedIdRef.current)),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        setIsSaving(true);

        let spouseId = values.spouse_id || null;
        if (fields.spouse_id?.enabled && values.civil_status_id === marriedId && values.spouse_id === OTHER_PERSON_ID) {
          const personTypes = await getPersonType().unwrap();
          const conyugeType = personTypes.find((item) => item.code === PERSON_CATALOG.conyuge_aval.code);
          if (!conyugeType) throw new Error("No se encontró el tipo de persona cónyuge");

          const created = await postPerson({
            body: {
              name: values.spouse_name,
              last_name: values.spouse_last_name,
              last_name_2: values.spouse_last_name_2 || undefined,
              loan_application_id: application.id,
              client_details_id: clientDetails.id,
              person_types: [conyugeType.id],
            },
          }).unwrap();
          spouseId = created.data.id;
        }

        const payload = buildPersonPatchPayload({
          values,
          fields,
          isMarried: values.civil_status_id === marriedId,
          spouseId,
        });

        if (!person) return;

        const patched = await patchPerson({ personId: person.id, body: payload }).unwrap();

        if (fields.domicilio_particular?.enabled) {
          await saveAddress(values.particular, particularType, particularAddress);
        }
        if (fields.domicilio_operativo?.enabled) {
          await saveAddress(values.operativo, operativoType, operativoAddress);
        }

        goBack({
          ...person,
          ...payload,
          ...(patched.data || {}),
          spouse_id: spouseId || person.spouse_id,
          personal_information_status: "COMPLETO",
        } as PersonaStructure);
      } catch (error) {
        const apiError = error as ApiError;
        if (apiError.status === 409) {
          formik.setFieldError("email", "El correo electrónico ya se encuentra registrado");
          return;
        }
        Sentry.captureException(error, { extra: { component: "ExpFormularioPersona-submit" } });
        dispatch(setModalErrorVisible({ open: true, error: apiError }));
      } finally {
        setIsSaving(false);
        setSubmitting(false);
      }
    },
  });

  const goBack = useCallback(
    (updated?: PersonaStructure) => {
      void updated;
      navigate(`${PATHS_EXPEDIENTE_V2.expediente.path}?tab=personas`);
    },
    [navigate],
  );

  const saveAddress = async (
    values: AddressFormValues,
    addressType: Address | undefined,
    existing: AddressData | undefined,
  ) => {
    if (!person?.id || !addressType?.id || !clientDetails?.id) return;
    const payload = buildAddressPayload({
      values,
      clientDetailsId: clientDetails.id,
      addressTypeId: addressType.id,
      personId: person.id,
    });
    if (existing?.id) {
      await patchAddress({ addressId: existing.id as UUID, data: payload }).unwrap();
    } else {
      await postAddress(payload).unwrap();
    }
  };

  const fetchLocationData = async (cp: string, prefix: "particular" | "operativo", coloniaId?: number) => {
    try {
      if (cp.length !== 5) return;
      const value = await getByCp(cp).unwrap();
      const { colonias, cities, states, localities } = value;

      formik.setFieldValue(`${prefix}.state_id`, String(states[0]?.id || ""));
      formik.setFieldValue(`${prefix}.state`, states[0]?.name || "");
      formik.setFieldValue(`${prefix}.city_id`, String(cities[0]?.id || ""));
      formik.setFieldValue(`${prefix}.city`, cities[0]?.name || "");
      formik.setFieldValue(`${prefix}.locality_id`, String(localities[0]?.id || ""));
      formik.setFieldValue(`${prefix}.locality`, localities[0]?.name || "");

      const list = (colonias || []).map((item: { id: number; name: string }) => ({
        id: item.id,
        name: item.name,
      }));
      if (prefix === "particular") setParticularColonias(list);
      else setOperativoColonias(list);

      if (coloniaId && coloniaId !== -1) {
        const selected = list.find((c: { id: number }) => c.id === coloniaId);
        formik.setFieldValue(`${prefix}.colonia_id`, selected ? String(selected.id) : "");
      } else if (coloniaId === -1) {
        formik.setFieldValue(`${prefix}.colonia_id`, OTHER_COLONIA_ID);
      } else {
        formik.setFieldValue(`${prefix}.colonia_id`, list[0] ? String(list[0].id) : "");
      }
    } catch (error) {
      Sentry.captureException(error, {
        extra: { component: "ExpFormularioPersona-fetchLocationData", prefix },
      });
    }
  };

  const loadData = useCallback(async () => {
    if (!personId || !application?.id || !application?.type) {
      goBack();
      return;
    }

    try {
      setIsLoading(true);

      const [personData, personsList, tabsConfig] = await Promise.all([
        getPersonById(personId).unwrap(),
        getPersons({ loanApplicationId: application.id }).unwrap(),
        getTabsConfig({ taxcode: application.type, code: BOFU_TABS_CONFIG.tab_personas.code }).unwrap(),
      ]);

      if (!personData) {
        goBack();
        return;
      }

      const resolvedFormsConfig = resolvePersonFormsConfig(findPersonasTab(tabsConfig)?.forms);
      const persons = personsList || [];

      // Compute fields to know which catalogs are needed
      const pTypeCodes = (personData.person_types_detail || []).map((t) => t.code);
      const principal = persons.find((p) => p.spouse_id === personData.id);
      const principalCodes = principal ? (principal.person_types_detail || []).map((t) => t.code) : [];
      const computedFields = resolveFieldsForPerson(
        pTypeCodes,
        resolvedFormsConfig ?? ({} as PersonFormsConfig),
        principalCodes,
      );

      const needsCivil =
        computedFields.civil_status_id?.enabled ||
        computedFields.matrimonial_regimes_id?.enabled ||
        computedFields.spouse_id?.enabled;
      const needsAddress = computedFields.domicilio_particular?.enabled || computedFields.domicilio_operativo?.enabled;

      const [civilRes, regimesRes, addressTypes, statesRes, addresses] = await Promise.all([
        needsCivil ? getCivilStatus().unwrap() : Promise.resolve([]),
        computedFields.matrimonial_regimes_id?.enabled ? getMatrimonialRegimes().unwrap() : Promise.resolve([]),
        needsAddress ? getAddressType().unwrap() : Promise.resolve([]),
        computedFields.birth_state?.enabled ? getStates().unwrap() : Promise.resolve([]),
        needsAddress ? getAddress({ personId: personData.id }).unwrap() : Promise.resolve([]),
      ]);

      const married = (civilRes || []).find((s) => s.name.toLowerCase() === "casado");
      const particularTypeItem = (addressTypes || []).find((t) => t.code === ADDRESS_CODES.particular.code);
      const operativoTypeItem = (addressTypes || []).find((t) => t.code === ADDRESS_CODES.operativo.code);
      const particularAddr = (addresses || []).find((a) => a.address_type_id === particularTypeItem?.id);
      const operativoAddr = (addresses || []).find((a) => a.address_type_id === operativoTypeItem?.id);

      setCivilStatuses(civilRes || []);
      setMarriedId(married?.id || "");
      marriedIdRef.current = married?.id || "";
      setRegimes(regimesRes || []);
      setStates(statesRes || []);
      setParticularType(particularTypeItem);
      setOperativoType(operativoTypeItem);
      setParticularAddress(particularAddr);
      setOperativoAddress(operativoAddr);
      setPerson(personData);
      setAllPersons(persons);
      setFormsConfig(resolvedFormsConfig);

      formik.setValues(buildInitialValues(personData, particularAddr, operativoAddr));

      if (particularAddr?.cp) {
        await fetchLocationData(
          particularAddr.cp,
          "particular",
          particularAddr.other_neighborhood ? -1 : particularAddr.colonia_id,
        );
      }
      if (operativoAddr?.cp) {
        await fetchLocationData(
          operativoAddr.cp,
          "operativo",
          operativoAddr.other_neighborhood ? -1 : operativoAddr.colonia_id,
        );
      }
    } catch (error) {
      Sentry.captureException(error, { extra: { component: "ExpFormularioPersona-loadData" } });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      goBack();
    } finally {
      setIsLoading(false);
    }
  }, [personId, application?.id, application?.type]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const showMarriedFields = formik.values.civil_status_id === marriedId && Boolean(marriedId);
  const disabled = isSaving || formik.isSubmitting;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
      <Box>
        <PageHeader
          eyebrow="Expediente · Personas relacionadas"
          title={"Información personal"}
          subtitle="Completa la información personal requerida para esta persona."
        />

        {isLoading ? (
          <Skeleton variant="rectangular" sx={{ height: ALTO_SKELETON_EXPEDIENTE, borderRadius: 2 }} />
        ) : !person || !formsConfig || !hasEnabledPersonFields(fields) ? (
          <Paper variant="lendia_paper" sx={{ p: "20px 22px" }}>
            <Typography color="primary.dark" textAlign="center">
              No hay campos configurados para los tipos de esta persona
            </Typography>
          </Paper>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "22px" }}>
            <Paper variant="lendia_paper" sx={{ p: "20px 22px" }}>
              <Box sx={FIELD_GRID}>
                <FieldWrapper label="Nombre completo" full>
                  <TextField fullWidth size="lendia_input" value={getPersonFullName(person)} disabled />
                </FieldWrapper>

                {fields.email?.enabled && (
                  <FieldWrapper
                    label="Correo electrónico"
                    error={formik.touched.email ? formik.errors.email : undefined}
                    full
                  >
                    <TextField
                      fullWidth
                      size="lendia_input"
                      name="email"
                      required={fields.email.required}
                      value={formik.values.email}
                      disabled={disabled}
                      inputProps={{ maxLength: 100 }}
                      error={formik.touched.email && Boolean(formik.errors.email)}
                      onChange={(e) => formik.setFieldValue("email", (e.target.value || "").trim().toLowerCase())}
                      onBlur={formik.handleBlur}
                    />
                  </FieldWrapper>
                )}

                {fields.cell_phone_number?.enabled && (
                  <FieldWrapper
                    label="Teléfono celular"
                    error={formik.touched.phone_number ? formik.errors.phone_number : undefined}
                    full
                  >
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <CountrySelect
                        value={formik.values.country_code}
                        onChange={(value) => formik.setFieldValue("country_code", value)}
                        disabled={disabled}
                      />
                      <TextField
                        fullWidth
                        size="lendia_input"
                        name="phone_number"
                        required={fields.cell_phone_number.required}
                        value={formik.values.phone_number}
                        disabled={disabled}
                        inputProps={{ maxLength: 10, inputMode: "numeric" }}
                        error={formik.touched.phone_number && Boolean(formik.errors.phone_number)}
                        onChange={(e) => formik.setFieldValue("phone_number", e.target.value.replace(/[^0-9]/g, ""))}
                        onBlur={formik.handleBlur}
                      />
                    </Box>
                  </FieldWrapper>
                )}

                {fields.rfc?.enabled && (
                  <FieldWrapper label="RFC" error={formik.touched.rfc ? formik.errors.rfc : undefined}>
                    <TextField
                      fullWidth
                      size="lendia_input"
                      name="rfc"
                      required={fields.rfc.required}
                      value={formik.values.rfc}
                      disabled={disabled}
                      inputProps={{ maxLength: 13 }}
                      error={formik.touched.rfc && Boolean(formik.errors.rfc)}
                      onChange={(e) => formik.setFieldValue("rfc", e.target.value.toUpperCase())}
                      onBlur={formik.handleBlur}
                    />
                  </FieldWrapper>
                )}

                {fields.curp?.enabled && (
                  <FieldWrapper label="CURP" error={formik.touched.curp ? formik.errors.curp : undefined}>
                    <TextField
                      fullWidth
                      size="lendia_input"
                      name="curp"
                      required={fields.curp.required}
                      value={formik.values.curp}
                      disabled={disabled}
                      inputProps={{ maxLength: 18 }}
                      error={formik.touched.curp && Boolean(formik.errors.curp)}
                      onChange={(e) => formik.setFieldValue("curp", e.target.value.toUpperCase())}
                      onBlur={formik.handleBlur}
                    />
                  </FieldWrapper>
                )}

                {fields.occupation?.enabled && (
                  <FieldWrapper
                    label="Ocupación"
                    error={formik.touched.occupation ? formik.errors.occupation : undefined}
                    full
                  >
                    <TextField
                      fullWidth
                      size="lendia_input"
                      name="occupation"
                      required={fields.occupation.required}
                      value={formik.values.occupation}
                      disabled={disabled}
                      error={formik.touched.occupation && Boolean(formik.errors.occupation)}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                  </FieldWrapper>
                )}

                {fields.birth_date?.enabled && (
                  <FieldWrapper
                    label="Fecha de nacimiento"
                    error={formik.touched.birth_date ? formik.errors.birth_date : undefined}
                  >
                    <DatePicker
                      value={formik.values.birth_date ? dayjs(formik.values.birth_date) : null}
                      disabled={disabled}
                      minDate={getFechaNacimientoMinima()}
                      onChange={(value: Dayjs | null) => {
                        formik.setFieldValue("birth_date", value ? value.format("YYYY-MM-DD") : "");
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: "lendia_input" as unknown as "small",
                          required: fields.birth_date.required,
                          error: formik.touched.birth_date && Boolean(formik.errors.birth_date),
                          onBlur: formik.handleBlur,
                          name: "birth_date",
                        },
                      }}
                    />
                  </FieldWrapper>
                )}

                {fields.birth_state?.enabled && (
                  <FieldWrapper
                    label="Lugar de nacimiento"
                    error={formik.touched.birth_state ? formik.errors.birth_state : undefined}
                  >
                    <Select
                      fullWidth
                      size="lendia_input"
                      displayEmpty
                      name="birth_state"
                      value={formik.values.birth_state}
                      disabled={disabled}
                      error={formik.touched.birth_state && Boolean(formik.errors.birth_state)}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    >
                      {states.map((state) => (
                        <MenuItem key={state.id} value={String(state.id)}>
                          {state.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FieldWrapper>
                )}

                {fields.sex?.enabled && (
                  <FieldWrapper label="Sexo" error={formik.touched.sex ? formik.errors.sex : undefined}>
                    <Select
                      fullWidth
                      size="lendia_input"
                      displayEmpty
                      name="sex"
                      value={formik.values.sex}
                      disabled={disabled}
                      error={formik.touched.sex && Boolean(formik.errors.sex)}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    >
                      <MenuItem value="MASCULINO">Masculino</MenuItem>
                      <MenuItem value="FEMENINO">Femenino</MenuItem>
                    </Select>
                  </FieldWrapper>
                )}

                {fields.civil_status_id?.enabled && (
                  <FieldWrapper
                    label="Estado civil"
                    error={formik.touched.civil_status_id ? formik.errors.civil_status_id : undefined}
                  >
                    <Select
                      fullWidth
                      size="lendia_input"
                      displayEmpty
                      name="civil_status_id"
                      value={formik.values.civil_status_id}
                      disabled={disabled}
                      error={formik.touched.civil_status_id && Boolean(formik.errors.civil_status_id)}
                      onChange={(e) => {
                        formik.handleChange(e);
                        if (e.target.value !== marriedId) {
                          formik.setFieldValue("matrimonial_regimes_id", "");
                          formik.setFieldValue("spouse_id", "");
                        }
                      }}
                      onBlur={formik.handleBlur}
                    >
                      {civilStatuses.map((status) => (
                        <MenuItem key={status.id} value={status.id}>
                          {status.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FieldWrapper>
                )}

                {fields.matrimonial_regimes_id?.enabled && showMarriedFields && (
                  <FieldWrapper
                    label="Régimen matrimonial"
                    error={formik.touched.matrimonial_regimes_id ? formik.errors.matrimonial_regimes_id : undefined}
                  >
                    <Select
                      fullWidth
                      size="lendia_input"
                      displayEmpty
                      name="matrimonial_regimes_id"
                      value={formik.values.matrimonial_regimes_id}
                      disabled={disabled}
                      error={formik.touched.matrimonial_regimes_id && Boolean(formik.errors.matrimonial_regimes_id)}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    >
                      {regimes.map((regime) => (
                        <MenuItem key={regime.id} value={regime.id}>
                          {regime.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FieldWrapper>
                )}

                {fields.spouse_id?.enabled && showMarriedFields && (
                  <>
                    <FieldWrapper
                      label="Cónyuge"
                      error={formik.touched.spouse_id ? formik.errors.spouse_id : undefined}
                      full
                    >
                      <Select
                        fullWidth
                        size="lendia_input"
                        displayEmpty
                        name="spouse_id"
                        value={formik.values.spouse_id}
                        disabled={disabled}
                        error={formik.touched.spouse_id && Boolean(formik.errors.spouse_id)}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      >
                        {spouseOptions.map((option) => (
                          <MenuItem key={option.id} value={option.id}>
                            {option.id === OTHER_PERSON_ID ? "Otro" : getPersonFullName(option)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FieldWrapper>

                    {formik.values.spouse_id === OTHER_PERSON_ID && (
                      <>
                        <FieldWrapper
                          label="Nombre"
                          error={formik.touched.spouse_name ? formik.errors.spouse_name : undefined}
                        >
                          <TextField
                            fullWidth
                            size="lendia_input"
                            name="spouse_name"
                            required
                            value={formik.values.spouse_name}
                            disabled={disabled}
                            error={formik.touched.spouse_name && Boolean(formik.errors.spouse_name)}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                        </FieldWrapper>

                        <FieldWrapper
                          label="Apellido paterno"
                          error={formik.touched.spouse_last_name ? formik.errors.spouse_last_name : undefined}
                        >
                          <TextField
                            fullWidth
                            size="lendia_input"
                            name="spouse_last_name"
                            required
                            value={formik.values.spouse_last_name}
                            disabled={disabled}
                            error={formik.touched.spouse_last_name && Boolean(formik.errors.spouse_last_name)}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                        </FieldWrapper>

                        <FieldWrapper label="Apellido materno">
                          <TextField
                            fullWidth
                            size="lendia_input"
                            name="spouse_last_name_2"
                            value={formik.values.spouse_last_name_2}
                            disabled={disabled}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                        </FieldWrapper>
                      </>
                    )}
                  </>
                )}
              </Box>
            </Paper>

            {fields.domicilio_particular?.enabled && (
              <Paper variant="lendia_paper" sx={{ p: "20px 22px" }}>
                <AddressBlock
                  prefix="particular"
                  title="Domicilio particular"
                  formik={formik}
                  disabled={disabled}
                  required={Boolean(fields.domicilio_particular.required)}
                  colonias={particularColonias}
                  onCpLookup={fetchLocationData}
                />
              </Paper>
            )}

            {fields.domicilio_operativo?.enabled && (
              <Paper variant="lendia_paper" sx={{ p: "20px 22px" }}>
                <AddressBlock
                  prefix="operativo"
                  title="Domicilio operativo"
                  formik={formik}
                  disabled={disabled}
                  required={Boolean(fields.domicilio_operativo.required)}
                  colonias={operativoColonias}
                  onCpLookup={fetchLocationData}
                />
              </Paper>
            )}

            <StepActions
              onBack={() => goBack()}
              onNext={() => formik.handleSubmit()}
              backLabel="Regresar al inicio"
              nextLabel="Continuar"
              nextDisabled={!formik.isValid || isSaving || formik.isSubmitting}
              backTestId="persona-form-back-button"
              nextTestId="persona-form-submit-button"
            />
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  );
};
